import React, { ReactElement, useState } from 'react';

import { formatUsername, getClassNames } from '../../../../../utils/util';
import { APPROVED, ARCHIVED, DECLINED, PENDING } from '../../../../../utils/constants';

import { FileType } from '../../../../../types/common.types';
import { Comment, HashTag, Reaction } from '../../../../../types/entity.types';

import TribeCommentContainer from '../../../../../containers/programme/tribe/tribe-comment/TribeComment.container';
import TribeInputContainer from '../../../../../containers/programme/tribe/tribe-input/TribeInput.container';
import TribeFilesContainer from '../../../../../containers/programme/tribe/tribe-files/TribeFiles.container';

import TribeApprovalButton from '../tribe-approval-button/TribeApprovalButton';
import ProfilePicture from '../../../../common/profile-picture/ProfilePicture';
import TribeThumbnails from '../tribe-thumbnails/TribeThumbnails';

import { ReactComponent as LikeIcon } from '../../../../../resources/icons/like.svg';

import './tribe-comment.scss';

const timeSince = (date: string | Date): string => {
  const minuteInSeconds: number = 60;
  const hourInSeconds: number = minuteInSeconds * 60;
  const dayInSeconds: number = hourInSeconds * 24;
  const weekInSeconds: number = dayInSeconds * 7;

  const seconds: number = Math.floor(new Date().getTime() - new Date(date).getTime()) / 1000;

  let value: number = seconds / weekInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'week' : 'weeks'} ago`;
  }

  value = seconds / dayInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'day' : 'days'} ago`;
  }

  value = seconds / hourInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'hour' : 'hours'} ago`;
  }

  value = seconds / minuteInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'minute' : 'minutes'} ago`;
  }

  return 'A few seconds ago';
};

type Props = {
  status: string;
  comment: Comment;
  files: FileType[];
  profilePicture?: string;
  liked: boolean;
  comments: Comment[];
  hashtags: HashTag[];
  showReplies: boolean;
  postExternalReference?: string;
  reply: boolean;
  approval: boolean;
  createReaction: (value: string) => void;
  deleteReaction: (value: string) => void;
  refreshPost?: (value: string) => void;
  updateCommentStatus: (value: string, callbackFn: () => void) => void;
  evaluateSupportConversation: (value: string) => void;
  hierarchy: boolean;
  navigateToProfile: (value: string) => void;
};

const TribeComment = ({
  status,
  comment,
  files = [],
  profilePicture,
  liked,
  comments = [],
  hashtags,
  showReplies,
  postExternalReference,
  reply,
  approval,
  createReaction,
  deleteReaction,
  refreshPost,
  updateCommentStatus,
  evaluateSupportConversation,
  hierarchy,
  navigateToProfile
}: Props): ReactElement => {
  const [showReplyInput, setShowReplyInput] = useState<boolean>(false);
  const [approvalStatus, setApprovalStatus] = useState<string>(comment.status ?? '');

  const {
    externalReference,
    text,
    startDateTime,
    userName,
    reactionCount,
    awaitingApproval,
    systemUserExternalReference
  }: Comment = comment;

  const totalLikes: number = reactionCount?.find((reaction: Reaction): boolean => reaction.text === 'like')?.count || 0;

  const handleLikeClick = (): void => {
    if (status === ARCHIVED || approval) {
      return;
    }

    if (liked) {
      deleteReaction('like');
    } else {
      createReaction('like');
    }
  };

  const handleApprovalButtonClick = (status: string): void => {
    updateCommentStatus(status, (): void => setApprovalStatus(status));
  };

  return (
    <div className={getClassNames('tribe-comment', { reply })} data-testid="tribe-comment">
      <div>
        <ProfilePicture src={profilePicture} alt={userName} small />
        <div>
          <div className="tribe-comment__bubble-container">
            <div
              className={getClassNames('tribe-comment__bubble', {
                approval: approval && approvalStatus === PENDING
              })}
            >
              <button
                className="tribe-comment__bubble__name"
                onClick={(): void => navigateToProfile(userName as string)}
              >
                {formatUsername(userName)}
              </button>
              <p className="tribe-comment__bubble__text">{text}</p>
              <TribeThumbnails files={files} comment />
              <TribeFilesContainer files={files} comment />
            </div>
          </div>

          <div className={getClassNames('tribe-comment__actions', { disabled: status === ARCHIVED })}>
            <p>{timeSince(startDateTime ?? '')}</p>
            {!approval && status !== ARCHIVED && <div className="tribe-comment__actions__seperator" />}
            {status !== ARCHIVED && !approval && (
              <p onClick={(): void => setShowReplyInput((prevState: boolean): boolean => !prevState)}>Reply</p>
            )}
            {!awaitingApproval && <div className="tribe-comment__actions__seperator" />}
            {!awaitingApproval && (
              <div className="tribe-comment__actions__icon-container">
                <LikeIcon
                  className={getClassNames('tribe-comment__actions__icon-container__icon', {
                    liked,
                    disabled: status === ARCHIVED
                  })}
                  onClick={handleLikeClick}
                  data-testid="tribe-comment-like-icon"
                />
                <p>
                  {totalLikes} {totalLikes === 1 ? 'Like' : 'Likes'}
                </p>
              </div>
            )}
          </div>
          <div className="tribe-comment__approval-actions">
            {awaitingApproval && approvalStatus === PENDING && (
              <TribeApprovalButton text="APPROVE" onClick={(): void => handleApprovalButtonClick(APPROVED)} />
            )}
            {awaitingApproval && approvalStatus === PENDING && (
              <TribeApprovalButton text="DECLINE" onClick={(): void => handleApprovalButtonClick(DECLINED)} />
            )}
            {awaitingApproval && approvalStatus === APPROVED && (
              <TribeApprovalButton text="APPROVED" onClick={(): void => handleApprovalButtonClick(PENDING)} approved />
            )}
            {awaitingApproval && approvalStatus === DECLINED && (
              <TribeApprovalButton
                text="CONTACT"
                onClick={(): void => evaluateSupportConversation(systemUserExternalReference as string)}
              />
            )}
            {awaitingApproval && approvalStatus === DECLINED && (
              <TribeApprovalButton text="DECLINED" onClick={(): void => handleApprovalButtonClick(PENDING)} declined />
            )}
          </div>
        </div>
      </div>
      {showReplyInput && (
        <TribeInputContainer
          placeholder="Reply..."
          hashtags={hashtags}
          parentExternalReference={externalReference}
          onPostSuccess={(): void => {
            setShowReplyInput(false);
            refreshPost?.(postExternalReference as string);
          }}
          comment
        />
      )}
      {showReplies &&
        comments.map(
          (reply: Comment): ReactElement => (
            <TribeCommentContainer
              key={reply.externalReference}
              status={status}
              comment={reply}
              hashtags={hashtags}
              showReplies={showReplies}
              postExternalReference={postExternalReference}
              refreshPost={refreshPost}
              approval={approval}
              reply
              hierarchy={hierarchy}
            />
          )
        )}
    </div>
  );
};

export default TribeComment;
